/**
 * SIM-1502 — inline editor for universal (T1) skills: `AgentSkill` rows with `tenantId IS NULL`.
 * The UX is deliberately heavier than the T2 Sheet because edits affect every tenant, so the
 * affected row stays in place and reveals an inline form rather than a side panel. Same data flow
 * as the T2 SkillsSection — the existing hooks already accept `tenantId: string | null`.
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  EmptyState,
  Input,
  Label,
  QueryView,
  Textarea,
} from "@simplicity/ui";
import { useState } from "react";
import { notifyMutationError, notifyWarnings } from "./notify";
import type { AgentSkill } from "./types";
import { useCreateSkill, useDeleteSkill, useSkills, useUpdateSkill } from "./useDottieContext";

interface FormState {
  name: string;
  description: string;
  body: string;
  reason: string;
}

const EMPTY_FORM: FormState = { name: "", description: "", body: "", reason: "" };

function toForm(row: AgentSkill): FormState {
  return { name: row.name, description: row.description, body: row.body, reason: "" };
}

function SkillInlineForm({
  initial,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
  idPrefix,
}: {
  initial: FormState;
  submitLabel: string;
  pending: boolean;
  onSubmit: (form: FormState) => void;
  onCancel: () => void;
  idPrefix: string;
}) {
  const [form, setForm] = useState<FormState>(initial);
  return (
    <div className="space-y-3 rounded-md border border-border bg-muted/20 p-3">
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-name`}>Name</Label>
        <Input
          id={`${idPrefix}-name`}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-description`}>Description</Label>
        <Textarea
          id={`${idPrefix}-description`}
          rows={2}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-body`}>Body (markdown)</Label>
        <Textarea
          id={`${idPrefix}-body`}
          rows={8}
          value={form.body}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-reason`}>Reason (optional)</Label>
        <Input
          id={`${idPrefix}-reason`}
          value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
          placeholder="Why are you making this change?"
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={() => onSubmit(form)}
          disabled={pending || form.name.trim() === ""}
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}

function SkillRow({
  row,
  isEditing,
  onStartEdit,
  onCancelEdit,
  onRequestDelete,
  onSave,
  pending,
}: {
  row: AgentSkill;
  isEditing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onRequestDelete: () => void;
  onSave: (form: FormState) => void;
  pending: boolean;
}) {
  return (
    <li className="rounded-md border border-border p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="font-medium text-foreground">{row.name}</div>
          <div className="text-sm text-muted-foreground">{row.description}</div>
        </div>
        <div className="flex shrink-0 gap-2">
          {!isEditing && (
            <Button variant="outline" size="sm" onClick={onStartEdit}>
              Edit
            </Button>
          )}
          <Button variant="destructive" size="sm" onClick={onRequestDelete}>
            Delete
          </Button>
        </div>
      </div>
      {isEditing && (
        <div className="mt-3">
          <SkillInlineForm
            initial={toForm(row)}
            submitLabel="Save"
            pending={pending}
            onSubmit={onSave}
            onCancel={onCancelEdit}
            idPrefix={`u-skill-edit-${row.id}`}
          />
        </div>
      )}
    </li>
  );
}

interface RenderArgs {
  rows: AgentSkill[];
  editingId: string | null;
  setEditingId: (id: string | null) => void;
  setDeleteTarget: (row: AgentSkill | null) => void;
  onSave: (id: string, form: FormState) => void;
  pending: boolean;
}

function renderRows({
  rows,
  editingId,
  setEditingId,
  setDeleteTarget,
  onSave,
  pending,
}: RenderArgs) {
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No universal skills"
        description="Add a skill that every tenant inherits."
      />
    );
  }
  return (
    <ul className="space-y-2">
      {rows.map((row) => (
        <SkillRow
          key={row.id}
          row={row}
          isEditing={editingId === row.id}
          onStartEdit={() => setEditingId(row.id)}
          onCancelEdit={() => setEditingId(null)}
          onRequestDelete={() => setDeleteTarget(row)}
          onSave={(form) => onSave(row.id, form)}
          pending={pending}
        />
      ))}
    </ul>
  );
}

function useUniversalSkillsState(
  setCreating: (v: boolean) => void,
  setEditingId: (v: string | null) => void,
  setDeleteTarget: (v: AgentSkill | null) => void,
  deleteTarget: AgentSkill | null,
) {
  const createSkill = useCreateSkill(null);
  const updateSkill = useUpdateSkill(null);
  const deleteSkill = useDeleteSkill(null);

  const handleCreate = (form: FormState) =>
    createSkill.mutate(
      {
        tenantId: null,
        name: form.name,
        description: form.description,
        body: form.body,
        reason: form.reason || undefined,
      },
      {
        onSuccess: (res) => {
          notifyWarnings(res.warnings);
          setCreating(false);
        },
        onError: (err) => notifyMutationError("Create universal skill", err),
      },
    );

  const handleUpdate = (id: string, form: FormState) =>
    updateSkill.mutate(
      {
        id,
        name: form.name,
        description: form.description,
        body: form.body,
        reason: form.reason || undefined,
      },
      {
        onSuccess: (res) => {
          notifyWarnings(res.warnings);
          setEditingId(null);
        },
        onError: (err) => notifyMutationError("Update universal skill", err),
      },
    );

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteSkill.mutate(
      { id: deleteTarget.id },
      {
        onSuccess: () => setDeleteTarget(null),
        onError: (err) => notifyMutationError("Delete universal skill", err),
      },
    );
  };

  return { createSkill, updateSkill, handleCreate, handleUpdate, handleDelete };
}

export function UniversalSkillsEditor() {
  const skillsQuery = useSkills(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AgentSkill | null>(null);

  const { createSkill, updateSkill, handleCreate, handleUpdate, handleDelete } =
    useUniversalSkillsState(setCreating, setEditingId, setDeleteTarget, deleteTarget);

  return (
    <section className="space-y-3" aria-labelledby="universal-skills-heading">
      <div className="flex items-center justify-between">
        <h3 id="universal-skills-heading" className="text-lg font-semibold text-foreground">
          Universal skills
        </h3>
        {!creating && (
          <Button size="sm" onClick={() => setCreating(true)}>
            Add universal skill
          </Button>
        )}
      </div>

      {creating && (
        <SkillInlineForm
          initial={EMPTY_FORM}
          submitLabel="Create"
          pending={createSkill.isPending}
          onSubmit={handleCreate}
          onCancel={() => setCreating(false)}
          idPrefix="u-skill-new"
        />
      )}

      <QueryView
        query={skillsQuery}
        error={(err) => (
          <p className="text-sm text-destructive" role="alert">
            Failed to load universal skills: {err.message}
          </p>
        )}
      >
        {(rows) =>
          renderRows({
            rows,
            editingId,
            setEditingId,
            setDeleteTarget,
            onSave: handleUpdate,
            pending: updateSkill.isPending,
          })
        }
      </QueryView>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete universal skill?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the skill for every tenant. A revision is recorded so the prior content
              stays viewable in History.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
